/**
 * Permissions Composable
 *
 * Provides reusable logic for checking user module permissions
 * and determining route access based on the centralized module configuration.
 *
 * This composable bridges the gap between:
 * - User's allowed modules (from auth store)
 * - Centralized module configuration
 * - Route paths
 *
 * Key Functions:
 * - getUserModuleIds(): Get list of module IDs the user has access to
 * - hasModuleAccess(): Check if user can access a specific module
 * - canAccessRoute(): Check if user can access a specific route
 * - getFilteredModuleConfigs(): Get module configs filtered by user permissions
 * - getReportItemsForUserModules(): Generate report subitems based on available modules
 */

import type { NavigationMenuItem } from '@nuxt/ui'
import {
  getAllModuleConfigs,
  getFooterModuleConfigs,
  getModuleConfigByRoute,
} from '~/config/modules.config'
import type { SpecialModuleId } from '~/types/navigation'
import { SystemModuleId } from '~/types/navigation'

/**
 * Get the list of module IDs that the current user has access to
 * Based on the modules assigned to their current workspace branch
 *
 * @returns Array of module IDs (e.g., ['CLN', 'SRV', 'INV'])
 */
export function getUserModuleIds(): string[] {
  const authStore = useAuthStore()

  if (!authStore.user) {
    return []
  }

  const currentBranchId = authStore.user.workspace.branch.id

  // Find the current branch in the user's accesses
  for (const access of authStore.user.profile.acceses) {
    const branch = access.branches.find(b => b.id === currentBranchId)
    if (branch) {
      return branch.modules.map(m => m.id)
    }
  }

  return []
}

/**
 * Check if the current user has access to a specific module
 *
 * @param moduleId - The module ID to check (e.g., 'CLN', 'SRV')
 * @returns true if user has access, false otherwise
 */
export function hasModuleAccess(
  moduleId: SystemModuleId | SpecialModuleId | string
): boolean {
  const userModules = getUserModuleIds()
  return userModules.includes(moduleId)
}

/**
 * Check if the current user can access a specific route
 * based on the route-to-module mapping in the centralized config
 *
 * Special cases:
 * - Root path ('/') is always accessible (dashboard)
 * - Routes not mapped to any module are accessible (e.g., profile, settings)
 * - Auth routes are handled by auth middleware, not this function
 *
 * @param routePath - The route path to check (e.g., '/customers', '/sales/new')
 * @returns true if user can access the route, false otherwise
 */
export function canAccessRoute(routePath: string): boolean {
  // Root path (dashboard) is always accessible for authenticated users
  if (routePath === '/' || routePath === '') {
    return true
  }

  // Find the module that owns this route
  const moduleConfig = getModuleConfigByRoute(routePath)

  // If no module is mapped to this route, allow access
  // (e.g., profile, settings, or other non-module routes)
  if (!moduleConfig) {
    return true
  }

  // Special modules (like management) are always accessible
  if (moduleConfig.isSpecial) {
    return true
  }

  // Check if user has access to this module
  return hasModuleAccess(moduleConfig.id)
}

/**
 * Get all module configurations filtered by user's permissions
 * Only returns modules the user has access to
 * Does not include footer modules by default
 *
 * @param includeFooter - Whether to include footer modules (default: false)
 * @returns Array of module configs the user can access, sorted by order
 */
export function getFilteredModuleConfigs(includeFooter = false) {
  const userModules = getUserModuleIds()
  const allModules = getAllModuleConfigs(includeFooter)

  return allModules.filter(config => {
    // Check display condition if present
    if (config.displayCondition && !config.displayCondition()) {
      return false
    }

    // Special modules (like management) are always accessible
    if (config.isSpecial) {
      return true
    }

    // Check if user has access to this module
    return userModules.includes(config.id)
  })
}

/**
 * Get footer module configurations
 * These are always shown regardless of module permissions
 *
 * @returns Array of footer module configs sorted by order
 */
export function getFilteredFooterModuleConfigs() {
  const configs = getFooterModuleConfigs()

  return configs.filter(config => {
    // Check display condition if present
    if (config.displayCondition && !config.displayCondition()) {
      return false
    }
    return true
  })
}

/**
 * Generate report navigation items based on user's available modules
 * Each module gets a corresponding report item if the user has access to it
 *
 * Report items mapping:
 * - Customers (CLN) → /reports/customers
 * - Services (SRV) → /reports/services
 * - Sales/Invoices (INV) → /reports/sales
 * - Accounting (ACC) → /reports/accounting
 *
 * @returns Array of navigation menu items for reports
 */
export function getReportItemsForUserModules(): NavigationMenuItem[] {
  const userModules = getUserModuleIds()
  const items: NavigationMenuItem[] = []

  // Map of module IDs (UUIDs) to their report items
  const reportItemsMap: Record<string, NavigationMenuItem> = {
    [SystemModuleId.Services]: {
      icon: 'i-lucide-file-text',
      label: 'Servicios',
      to: '/reports/services',
      ui: {
        linkLeadingIcon: 'size-4',
      },
    },
    [SystemModuleId.Invoices]: {
      icon: 'i-lucide-circle-dollar-sign',
      label: 'Ventas',
      to: '/reports/sales',
      ui: {
        linkLeadingIcon: 'size-4',
      },
    },
    [SystemModuleId.Purchases]: {
      icon: 'i-lucide-shopping-cart',
      label: 'Compras',
      to: '/reports/purchases',
      ui: {
        linkLeadingIcon: 'size-4',
      },
    },
    [SystemModuleId.Accounting]: {
      icon: 'i-lucide-calculator',
      label: 'Contabilidad',
      to: '/reports/accounting',
      ui: {
        linkLeadingIcon: 'size-4',
      },
    },
  }

  // Add report items for modules the user has access to
  for (const moduleId of userModules) {
    if (reportItemsMap[moduleId]) {
      items.push(reportItemsMap[moduleId])
    }
  }

  return items
}

/**
 * Composable hook for permissions
 * Returns an object with all permission-related functions
 *
 * Usage:
 * ```ts
 * const { hasModuleAccess, canAccessRoute } = usePermissions()
 * if (canAccessRoute('/customers')) {
 *   // Navigate to customers
 * }
 * ```
 */
export function usePermissions() {
  return {
    getUserModuleIds,
    hasModuleAccess,
    canAccessRoute,
    getFilteredModuleConfigs,
    getFilteredFooterModuleConfigs,
    getReportItemsForUserModules,
  }
}
